import React from 'react';
import './Comparison_Enhanced.css';

const CheckIcon = () => (
  <svg className="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ComparisonEnhanced = () => {
  // 3-way comparison: Manual, Traditional Automation, Qate AI
  const comparisons = [
    {
      feature: "Test creation time",
      manual: "Hours per test",
      traditional: "Days to weeks",
      ai: "Minutes",
      aiHighlight: true
    },
    {
      feature: "Coding required",
      manual: "No",
      traditional: "Yes",
      ai: "No",
      aiHighlight: true
    },
    {
      feature: "Natural language support",
      manual: null,
      traditional: null,
      ai: true,
      aiHighlight: true
    },
    {
      feature: "Test maintenance",
      manual: "Re-execute",
      traditional: "Manual fixes",
      traditionalCite: { num: 1, url: "https://www.rainforestqa.com/blog/test-automation-maintenance" },
      ai: "AI healing suggestions",
      aiCite: { num: 3, url: "https://www.accelq.com/blog/self-healing-test-automation/" },
      aiHighlight: true
    },
    {
      feature: "Failure analysis",
      manual: "Human judgment",
      traditional: "Pass/fail only",
      ai: "AI-powered insights",
      aiHighlight: true
    },
    {
      feature: "Platform coverage",
      manual: "Any",
      traditional: "Web only",
      ai: "Web, Desktop, API, SOAP",
      aiHighlight: true
    },
    {
      feature: "Team accessibility",
      manual: "Anyone",
      traditional: "Developers only",
      ai: "Everyone",
      aiCite: { num: 4, url: null },
      aiHighlight: true
    },
    {
      feature: "Scales with releases",
      manual: null,
      traditional: true,
      ai: true,
      aiHighlight: false
    },
    {
      feature: "CI/CD integration",
      manual: null,
      traditional: true,
      ai: true,
      aiHighlight: false
    },
    {
      feature: "Bug reporting integration",
      manual: "Manual",
      traditional: "Manual",
      ai: "Automatic to Jira",
      aiHighlight: true
    },
    {
      feature: "Compatibility with",
      manual: "Any",
      traditional: "Limited frameworks",
      ai: "All",
      aiHighlight: true
    },
    {
      feature: "Average monthly cost",
      manual: "$5,000+",
      manualCite: { num: 2, url: "https://www.ziprecruiter.com/Salaries/Qa-Tester-Salary" },
      traditional: "$500+ engineering time",
      ai: "Just your credits",
      aiHighlight: true,
      isCost: true
    },
  ];

  const renderValue = (value, isAi = false, highlight = false, cite = null) => {
    if (value === true) {
      return <CheckIcon />;
    }
    if (value === false || value === null) {
      return <span className="value-dash">—</span>;
    }
    return (
      <span className={`value-text ${isAi && highlight ? 'highlight' : ''}`}>
        {value}
        {cite && (
          cite.url ? (
            <a
              href={cite.url}
              target="_blank"
              rel="noopener noreferrer"
              className="cite"
              title="View source"
            >
              {cite.num}
            </a>
          ) : (
            <span className="cite">{cite.num}</span>
          )
        )}
      </span>
    );
  };

  return (
    <section className="comparison-enhanced">
      <div className="comparison-container-enhanced">

        {/* Header */}
        <div className="comparison-header-enhanced">
          <h2>Compare Testing Approaches</h2>
          <p>See why teams are switching to AI-powered testing</p>
        </div>

        {/* Main Comparison Table */}
        <div className="comparison-table-modern">

          {/* Table Headers */}
          <div className="table-headers">
            <div className="header-feature">
              <span className="header-feature-text">Feature</span>
            </div>
            <div className="header-traditional">
              <h3>Traditional E2E</h3>
            </div>
            <div className="header-manual">
              <h3>Manual</h3>
            </div>
            <div className="header-ai">
              <h3>Qate AI</h3>
            </div>
          </div>

          {/* Comparison Rows */}
          {comparisons.map((item, index) => (
            <div key={index} className={`feature-row ${item.isCost ? 'cost-row' : ''}`}>
              <div className="feature-name">{item.feature}</div>
              <div className="feature-value traditional">
                {renderValue(item.traditional, false, false, item.traditionalCite)}
              </div>
              <div className="feature-value manual">
                {renderValue(item.manual, false, false, item.manualCite)}
              </div>
              <div className="feature-value ai">
                {renderValue(item.ai, true, item.aiHighlight, item.aiCite)}
              </div>
            </div>
          ))}
        </div>

        {/* Sources */}
        <div className="sources-footnotes">
          <p className="sources-title">Sources</p>
          <ol>
            <li>
              <a href="https://www.rainforestqa.com/blog/test-automation-maintenance" target="_blank" rel="noopener noreferrer">
                Rainforest QA - Test Automation Maintenance Survey
              </a> — 55% of teams spend 20+ hrs/week on test maintenance
            </li>
            <li>
              <a href="https://www.ziprecruiter.com/Salaries/Qa-Tester-Salary" target="_blank" rel="noopener noreferrer">
                ZipRecruiter - QA Tester Salary Data
              </a> — Average hourly rate $42.95
            </li>
            <li>
              <a href="https://www.accelq.com/blog/self-healing-test-automation/" target="_blank" rel="noopener noreferrer">
                ACCELQ - Self-Healing Test Automation
              </a> — 80-90% maintenance reduction
            </li>
            <li>
              Gartner — 70% of new enterprise apps will use no-code/low-code by 2025
            </li>
          </ol>
        </div>
      </div>
    </section>
  );
};

export default ComparisonEnhanced;